import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import Layout from './components/Layout';
import Login from './components/Login';
import Auth from './components/Auth';
import AccountSettings from './components/AccountSettings';
import History from './components/History';
import Account from './pages/Account';
import AuthCallback from './pages/AuthCallback';
import NewCoverLetterPage from './pages/NewCoverLetterPage';
import Home from './components/Home';
import { ThemeProvider } from './components/ThemeProvider';
import { MaterialThemeProvider } from './components/MuiThemeProvider';
import { Toaster } from './components/ui/toaster';

// Import the global CSS for Tailwind/ShadCN - remove this import if it's added in index.tsx
// import './globals.css';

const AuthCodeHandler = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code');
  const { user } = useAuth();

  useEffect(() => {
    // If user is already authenticated, don't handle auth codes
    if (user) {
      return;
    }
    
    // Skip redirecting if we're already on the auth-callback route
    if (code && !location.pathname.includes('/auth-callback')) {
      // Preserve all search params when redirecting to auth-callback
      navigate(`/auth-callback${location.search}`, { replace: true });
    }
  }, [code, navigate, location.search, location.pathname, user]);

  // Only intercept routes with a code parameter, otherwise pass through
  return code && !user ? null : <Outlet />;
};

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return null; // Or a loading spinner
  }

  if (!user) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

function App() {
  return (
    <div className="min-h-screen bg-background">
      <Router>
        <ThemeProvider>
          <MaterialThemeProvider>
            <AuthProvider>
              <SubscriptionProvider>
                <Routes>
                {/* Move AuthCodeHandler to wrap all routes instead of being a fallback route */}
                <Route element={<AuthCodeHandler />}>
                  {/* Basic routes */}
                  <Route path="/" element={<Home />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/auth-callback" element={<AuthCallback />} />
                  
                  {/* Simple URL handling routes - redirect to /new with jobUrl */}
                  <Route path="/https://*" element={<Navigate to={`/new?jobUrl=${encodeURIComponent(`https://${window.location.pathname.substring(9)}${window.location.search}`)}`} replace />} />
                  <Route path="/http://*" element={<Navigate to={`/new?jobUrl=${encodeURIComponent(`http://${window.location.pathname.substring(8)}${window.location.search}`)}`} replace />} />
                  
                  {/* NewCoverLetterPage will handle auth check internally */}
                  <Route path="/new" element={<NewCoverLetterPage />} />
                  
                  {/* Protected routes */}
                  <Route path="/dashboard" element={<PrivateRoute><Layout><History /></Layout></PrivateRoute>} />
                  <Route path="/profile" element={<PrivateRoute><Layout><AccountSettings /></Layout></PrivateRoute>} />
                  <Route path="/account" element={<PrivateRoute><Layout><Account /></Layout></PrivateRoute>} />
                  
                  {/* Catch-all route */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
                <Toaster />
              </SubscriptionProvider>
            </AuthProvider>
          </MaterialThemeProvider>
        </ThemeProvider>
      </Router>
    </div>
  );
}

export default App;
